import type { FunctionComponent } from "react";
import type React from "react";

import styled from "styled-components";

import { Autocomplete, DotsLoader, ListItemKeyword, ListItemSeparator } from "@similarweb/ui-components";

import { getAutocompleteRecentKeywordSearches } from "pro-features/components/AutocompleteKeywords/helpers/KeywordAutocompleteDataHandler";
import type { IAutocompleteKeyword } from "pro-features/components/AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";
import type { ISite } from "pro-features/components/Workspace/Wizard/src/types";
import { i18nFilter } from "app/filters/i18nFilter";
import { DefaultFetchService } from "app/services/fetchService";
const fetchService = DefaultFetchService.getInstance();

export const AutocompleteStyled = styled(Autocomplete)`
    width: 100%;

    &.autocomplete-open {
        z-index: 20;
    }
`;

interface IAutocompleteEmptyState {
    emptyStateKeywords?: IAutocompleteKeyword[];
    emptyStateTitle?: string;
    hideEmptyState?: boolean;
}

interface IAutocompleteKeywordsProps {
    children?: React.ReactNode;
    selectedValue?: string;
    onClick: (selectedValue: string) => void;
    autocompleteProps: any;
    onValueChange?: (value: string) => void;
    emptyState?: IAutocompleteEmptyState;
    numOfQueryItems?: number;
    exclude?: string[];
    autocompleteRef?: React.Ref<any>;
    countryIdAccessor?: () => number;
}

export const AutocompleteKeywords: FunctionComponent<IAutocompleteKeywordsProps> = ({
    selectedValue,
    autocompleteProps = {},
    onClick,
    onValueChange = () => {},
    emptyState,
    numOfQueryItems = 9,
    exclude = [],
    autocompleteRef,
}) => {
    const i18n = i18nFilter();
    const getAutocompleteResults = async (query: string) => {
        return await fetchService.get<ISite[]>(
            `/autocomplete/keywords?size=${numOfQueryItems}&term=${query}&webSource=Desktop&validate=true`,
        );
    };
    const onAutocompleteItemClick = (name: string) => () => {
        onClick(name);
    };
    const onAutocompleteGetData = async (query: string) => {
        onValueChange(query);
        if (query === "") {
            const distinctKeywordsRecentSearches = getAutocompleteRecentKeywordSearches();
            if (!distinctKeywordsRecentSearches.length && emptyState) {
                if (emptyState.hideEmptyState) {
                    return [];
                }
                // @ts-ignore FIXME: strict migration - 'emptyState.emptyStateKeywords' is possibly 'undefined' — callers always provide emptyStateKeywords whenever an emptyState object is passed
                return emptyState.emptyStateKeywords.reduce(
                    (results, { name }) => {
                        const item = (
                            <ListItemKeyword
                                iconName="search-keywords"
                                key={name}
                                text={name}
                                onClick={onAutocompleteItemClick(name)}
                            />
                        );
                        return [...results, item];
                    },
                    [
                        <ListItemSeparator key="AutocompleteKeywordsListItemSeparator">
                            {emptyState.emptyStateTitle}
                        </ListItemSeparator>,
                    ],
                );
            }
            return distinctKeywordsRecentSearches
                .filter(({ name }) => !exclude.includes(name))
                .reduce(
                    (results, { name }) => {
                        const item = (
                            <ListItemKeyword
                                iconName="search-keywords"
                                key={name}
                                text={name}
                                onClick={onAutocompleteItemClick(name)}
                            />
                        );
                        return [...results, item];
                    },
                    [
                        <ListItemSeparator key="AutocompleteKeywordsListItemSeparator">
                            {i18n("autocompleteKeywordsGroup.recentsTitle")}
                        </ListItemSeparator>,
                    ],
                );
        } else {
            const results = await getAutocompleteResults(query);
            return results
                .filter(({ name }) => !exclude.includes(name))
                .map((item) => {
                    const { name, image } = item;
                    return (
                        <ListItemKeyword
                            iconName="search-keywords"
                            key={name}
                            text={name}
                            onClick={onAutocompleteItemClick(name)}
                        />
                    );
                });
        }
    };
    const onKeyUp = (e: FixmeType) => {
        const { onKeyUp = () => null } = autocompleteProps;
        if (e.target.value === "") {
            onClick("");
        }
        onKeyUp(e);
    };

    return (
        <AutocompleteStyled
            className="AutocompleteKeywords"
            ref={autocompleteRef}
            getListItems={onAutocompleteGetData}
            loadingComponent={<DotsLoader />}
            floating={true}
            debounce={250}
            selectedValue={selectedValue}
            placeholder="Start typing here..."
            onKeyUp={onKeyUp}
            {...autocompleteProps}
        />
    );
};
